import { useQuery } from '@tanstack/react-query';
import { CircleDot, Satellite } from 'lucide-react';
import { QueryState, StatusPill, formatDate, formatNumber } from '@/components/live';
import { useRows } from '@/hooks/useResource';
import { callRpc } from '@/api/resources';
import { isSupabaseConfigured } from '@/lib/supabase';
import { signedUrlsFor } from '@/api/documents';

interface MonitoringRow {
  id: string; monitoring_code: string | null; plot_id: string; provider_key: string | null;
  monitoring_status: string; baseline_acquired_at: string | null; area_ha: number | null;
  last_checked_at: string | null; next_due_at: string | null; observation_count: number;
  open_event_count: number; geometry_version: number; closed_at: string | null; close_reason: string | null;
}

interface HarvestAssessmentRow {
  id: string; harvest_code: string | null; declared_harvest_date: string; verdict: string;
  verdict_reason: string | null; ndvi_before: number | null; ndvi_after: number | null;
  change_pct: number | null; observed_change_on: string | null; spatial_confidence: string | null;
  event_review_status: string | null; attempts: number;
}

interface TimelineRow {
  at: string; kind: string; title: string; detail: string | null; state: string | null;
  reference_id: string; document_id: string | null;
}

const KIND_LABEL: Record<string, string> = {
  enrollment: 'Enrolment', snapshot: 'Monthly evidence', event: 'Potential change', harvest: 'Harvest', request: 'Requested check',
};

/**
 * A plot's satellite monitoring, inside its own record (owner, 2026-09-22: "Allow users to view a
 * plot timeline: Enrollment → Monthly observations → LUC events → Harvest evidence").
 *
 * Two things it is careful about. A change is shown as *potential* until somebody has reviewed it,
 * because a satellite sees the ground and not the rule. And a month with no usable observation is
 * shown as a gap rather than left blank, so the reader can tell "nothing happened" from "we could
 * not see".
 */
export function PlotMonitoring({ plotId }: { plotId: string }) {
  const monitoring = useRows<MonitoringRow>('satellite_monitoring', { filters: { plot_id: plotId } });
  const harvests = useRows<HarvestAssessmentRow>('satellite_harvest_assessments', { filters: { plot_id: plotId } });
  const timeline = useQuery({
    queryKey: ['plot-satellite-timeline', plotId],
    enabled: isSupabaseConfigured && Boolean(plotId),
    queryFn: () => callRpc<TimelineRow[]>('fn_plot_satellite_timeline', { p_plot_id: plotId }),
  });

  // The pictures on the timeline: each month's evidence, and a request's latest scene (2026-09-28).
  const pictureIds = (timeline.data ?? []).map((row) => row.document_id).filter((id): id is string => Boolean(id));
  const pictures = useQuery({
    queryKey: ['plot-satellite-pictures', pictureIds.join(',')],
    enabled: isSupabaseConfigured && pictureIds.length > 0,
    queryFn: () => signedUrlsFor(pictureIds),
  });
  const current = (monitoring.data ?? []).find((row) => row.monitoring_status !== 'closed')
    ?? (monitoring.data ?? [])[0];
  const closed = (monitoring.data ?? []).filter((row) => row.monitoring_status === 'closed');

  return (
    <div className="plot-monitoring">
      {!current ? (
        <p className="muted">
          This plot is not monitored yet. Monitoring starts once its boundary is approved, and a baseline is
          taken from the satellite archive.
        </p>
      ) : (
        <>
          <dl className="detail-grid">
            <div><dt>Status</dt><dd><StatusPill value={current.monitoring_status} /></dd></div>
            <div><dt>Source</dt><dd>{current.provider_key ?? '--'}</dd></div>
            <div><dt>Baseline</dt>
              <dd>{current.baseline_acquired_at ? formatDate(current.baseline_acquired_at) : 'Not taken yet'}</dd></div>
            <div><dt>Observations</dt><dd>{formatNumber(current.observation_count)}</dd></div>
            <div><dt>Last checked</dt><dd>{current.last_checked_at ? formatDate(current.last_checked_at) : '--'}</dd></div>
            <div><dt>Next check due</dt><dd>{current.next_due_at ? formatDate(current.next_due_at) : '--'}</dd></div>
            <div><dt>Open for review</dt>
              <dd>{current.open_event_count > 0
                ? <strong style={{ color: 'var(--warn)' }}>{current.open_event_count}</strong>
                : 'None'}</dd></div>
            <div><dt>Boundary version</dt><dd>{current.geometry_version}</dd></div>
          </dl>

          {closed.length > 0 && (
            <p className="muted" style={{ marginTop: 0 }}>
              {closed.length} earlier monitoring {closed.length === 1 ? 'period' : 'periods'} on previous boundaries.
              Their evidence is kept: {closed.map((row) => row.close_reason).filter(Boolean).join('; ')}
            </p>
          )}

          {(harvests.data ?? []).length > 0 && (
            <>
              <h4 className="section-heading" style={{ marginTop: 16 }}>Declared harvests</h4>
              <ul className="dash-list">
                {(harvests.data ?? []).map((row) => (
                  <li key={row.id}>
                    <span className="cell-stack">
                      <span>{formatDate(row.declared_harvest_date)}{row.harvest_code ? ` · ${row.harvest_code}` : ''}</span>
                      <small className="muted">{row.verdict_reason ?? ''}</small>
                    </span>
                    <StatusPill value={row.verdict} />
                  </li>
                ))}
              </ul>
            </>
          )}

          <h4 className="section-heading" style={{ marginTop: 16 }}>Timeline</h4>
          <QueryState empty="Nothing recorded for this plot yet." query={timeline}>
            <ol className="sat-timeline">
              {(timeline.data ?? []).map((row) => (
                <li key={`${row.kind}-${row.reference_id}`}>
                  <div className="sat-mark">
                    {row.kind === 'event' ? <CircleDot size={13} /> : <Satellite size={13} />}
                  </div>
                  <div className="sat-body">
                    <p><strong>{row.title}</strong>{row.detail ? <> — {row.detail}</> : null}</p>
                    {row.document_id && pictures.data?.[row.document_id] && (
                      <a href={pictures.data[row.document_id]} rel="noreferrer" target="_blank" title="Open the picture">
                        <img alt={row.title} className="sat-thumb" src={pictures.data[row.document_id]} />
                      </a>
                    )}
                    <div className="sat-meta">
                      <span>{formatDate(row.at)}</span>
                      <span className="muted">{KIND_LABEL[row.kind] ?? row.kind}</span>
                      {row.state && <StatusPill value={row.state} />}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </QueryState>
          <p className="muted" style={{ fontSize: '12.5px' }}>
            Satellite observation is remote evidence. A change here is something to check against field records,
            never a finding on its own.
          </p>
        </>
      )}
    </div>
  );
}
